import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, getRouteApi, Outlet } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { InstrumentHeader } from '~/components/instrument/instrument-header.tsx';
import { InstrumentNavbar } from '~/components/instrument/instrument-navbar.tsx';
import { useHydrateInstrumentDisplayPayload } from '~/components/root/use-hydrate-instrument-display-payload.ts';
import { useWarmInstrumentDisplayData } from '~/components/root/use-warm-instrument-display-data.ts';
import { ScrollArea } from '~/components/shadcn/scroll-area.tsx';
import { Skeleton } from '~/components/shadcn/skeleton.tsx';
import type { PortfolioForLayoutType } from '~/database/supabase/portfolio/types.js';
import { useTranslations } from '~/i18n/use-translations.ts';
import type { UserInstrumentDisplayDataPayload } from '~/integrations/yahoo/instrument-display-data-payload.ts';
import { requireAuthenticatedBeforeLoad } from '~/routing/require-authenticated-before-load.ts';
import { getAppShellDataQueryOptions } from '~/server/app/app-shell-data-query.ts';
import { getInstrumentLayoutPageDataQueryOptions } from '~/server/instrument/instrument-layout-page-data-query.ts';
import { getInstrumentLayoutDisplayTickers } from '~/utils/instrument-display-tickers.ts';
import { useIsMobile } from '~/utils/start-client-hints.tsx';
import InstrumentContentSidebar from '~/views/instrument/components/content-sidebar.tsx';

export const Route = createFileRoute('/instrument/$ticker')({
	beforeLoad: requireAuthenticatedBeforeLoad,
	component: InstrumentLayoutPage,
	loader: ({ context, params }) =>
		context.queryClient.ensureQueryData(getInstrumentLayoutPageDataQueryOptions({ ticker: params.ticker })),
	pendingComponent: InstrumentLayoutPagePending,
	pendingMinMs: 250,
});

function InstrumentLayoutPagePending() {
	return (
		<div className="h-full w-full">
			<div className="shadow-inner">
				<div className="space-y-1 px-1 pt-1">
					<Skeleton className="h-20 w-full rounded-md border border-border bg-card" />
					<Skeleton className="h-10 w-full rounded-md border border-border bg-card" />
				</div>
			</div>
			<div className="p-1">
				<div className="grid h-instrument-body-height grid-cols-1 gap-2 xl:grid-cols-[minmax(0,1fr)_320px]">
					<Skeleton className="h-full w-full rounded-md border border-border bg-card" />
					<Skeleton className="hidden h-full w-full rounded-md border border-border bg-card xl:block" />
				</div>
			</div>
		</div>
	);
}

function getInstrumentRoutes(ticker: string | undefined, t: ReturnType<typeof useTranslations>) {
	const baseSlug = `/instrument/${ticker}`;
	return [
		{
			name: t('instrument.tab.overview'),
			to: `${baseSlug}`,
		},
		{
			name: t('instrument.tab.financials'),
			to: `${baseSlug}/financials`,
		},
		{
			name: t('instrument.tab.ownership'),
			to: `${baseSlug}/ownership`,
		},
	] as const;
}

function getPortfolioList(portfolios?: PortfolioForLayoutType[] | null) {
	return portfolios?.map((portfolio) => ({
		id: Number(portfolio?.id),
		instruments: portfolio?.position?.map((position) => ({
			id: Number(position.instrument_id),
			size: position.size,
		})),
		name: portfolio?.name,
	}));
}

function InstrumentLayoutPage() {
	const t = useTranslations();
	const { ticker } = Route.useParams();
	const { data } = useSuspenseQuery(getInstrumentLayoutPageDataQueryOptions({ ticker }));
	const { instrument, instrumentFetch, relatedInstruments, reports, userLocale } = data;
	const { data: rootLoaderData } = useSuspenseQuery(getAppShellDataQueryOptions());
	const [chosenReportId, setChosenReportId] = useState<number | null>(null);
	const [isSidebarOpen, setIsSidebarOpen] = useState(!!chosenReportId);
	const instrumentRoutes = getInstrumentRoutes(instrument?.ticker, t);
	const portfolioList = getPortfolioList(rootLoaderData.portfolios);
	const { watchlists } = rootLoaderData;
	const isMobile = useIsMobile();
	const initialInstrumentDisplayPayload = useMemo<UserInstrumentDisplayDataPayload | null>(() => {
		const instrumentTicker = instrument?.ticker;
		if (!instrumentTicker) {
			return null;
		}

		return {
			instrumentFetchEntries: instrumentFetch ? [[instrumentTicker, instrumentFetch]] : [],
			tickerList: [instrumentTicker],
		};
	}, [instrument?.ticker, instrumentFetch]);

	useWarmInstrumentDisplayData(
		getInstrumentLayoutDisplayTickers({
			instrument,
			relatedInstruments,
		}),
	);

	useHydrateInstrumentDisplayPayload(initialInstrumentDisplayPayload);

	useEffect(() => {
		if (!isSidebarOpen) {
			return;
		}

		function handleKeyDown(event: KeyboardEvent) {
			if (event.key === 'Escape') {
				setIsSidebarOpen(false);
				setChosenReportId(null);
			}
		}

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [isSidebarOpen]);

	const showWatchlist = instrument && watchlists;

	if (isMobile) {
		return (
			<ScrollArea
				classNames={{
					root: 'flex h-full w-body-width',
					viewport: 'flex h-body-height w-body-width [&>div]:block! [&>div]:h-fit',
				}}
			>
				{showWatchlist ? (
					<InstrumentHeader
						instrument={instrument}
						portfolioList={portfolioList}
						relatedInstruments={relatedInstruments}
						userLocale={userLocale}
						watchlists={watchlists}
					/>
				) : null}
				<div className="sticky top-0 z-10 flex bg-background px-1">
					<InstrumentNavbar
						instrumentRoutes={instrumentRoutes}
						isSidebarOpen={isSidebarOpen}
						setIsSidebarOpen={setIsSidebarOpen}
					/>
					{isSidebarOpen ? (
						<button
							aria-label={t('instrument.closeReportsOverlay')}
							className="absolute inset-x-0 top-[46px] bottom-0 z-20 bg-background/35 backdrop-blur-[1px]"
							onClick={() => {
								setIsSidebarOpen(false);
								setChosenReportId(null);
							}}
							type="button"
						/>
					) : null}
					<div className="absolute top-[46px] right-0 h-[calc(100dvh-110px)]">
						<InstrumentContentSidebar
							chosenReportId={chosenReportId}
							isSidebarOpen={isSidebarOpen}
							reports={reports}
							setChosenReportId={setChosenReportId}
							userLocale={userLocale}
						/>
					</div>
				</div>
				<div className="flex w-body-width flex-row p-1">
					<Outlet />
				</div>
			</ScrollArea>
		);
	}

	return (
		<div className="h-full w-full">
			<div className="shadow-inner">
				{showWatchlist ? (
					<>
						<InstrumentHeader
							instrument={instrument}
							portfolioList={portfolioList}
							relatedInstruments={relatedInstruments}
							userLocale={userLocale}
							watchlists={watchlists}
						/>
						<div className="px-1">
							<InstrumentNavbar
								instrumentRoutes={instrumentRoutes}
								isSidebarOpen={isSidebarOpen}
								setIsSidebarOpen={setIsSidebarOpen}
							/>
						</div>
					</>
				) : null}
			</div>
			<div className="relative">
				{isSidebarOpen ? (
					<button
						aria-label={t('instrument.closeReportsOverlay')}
						className="absolute inset-0 z-30 bg-background/30 backdrop-blur-[1px]"
						onClick={() => {
							setIsSidebarOpen(false);
							setChosenReportId(null);
						}}
						type="button"
					/>
				) : null}
				<ScrollArea
					classNames={{
						root: 'flex h-instrument-body-height',
						viewport: 'flex h-instrument-body-height p-1 sm:pr-3 [&>div]:block! [&>div]:w-body-margin',
					}}
				>
					<Outlet />
				</ScrollArea>
				<div className="pointer-events-none absolute inset-y-0 right-0 z-40 pr-2 pb-2 pl-6">
					<div className="pointer-events-auto h-full">
						<InstrumentContentSidebar
							chosenReportId={chosenReportId}
							isSidebarOpen={isSidebarOpen}
							reports={reports}
							setChosenReportId={setChosenReportId}
							userLocale={userLocale}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}

export const InstrumentRouteApi = getRouteApi('/instrument/$ticker');
